import { noteRenderWork, postRenderRequest, postUpdateGeometry } from "../worker/RenderWorkerManager.ts";
import useStore from "../store.ts";
import { getParent } from "./ElementUtils.ts";
import {
    collectAutoSizeAncestorsForMany,
    markForUpdate,
} from "./AutoSizeAncestorTracker.ts";
import updateCoordinator from "./UpdateCoordinator.ts";

/* see docs/EXTRACTED_COMMENTS.md#c201 */
export enum RenderInvalidationReason {
    LayoutLoaded = "layoutLoaded",
    GeometryChanged = "geometryChanged",
    DataMutation = "dataMutation",
    VisibilityChange = "visibilityChange",
    FontLoaded = "fontLoaded",
    PendingRowsCleared = "pendingRowsCleared",
    ContainerTemplateChange = "CONTAINER_TEMPLATE_CHANGE",
    ManualRefresh = "manualRefresh"
}

type InvalidationOptions = {
    /* see docs/EXTRACTED_COMMENTS.md#c202 */
    force?: boolean;
    /* see docs/EXTRACTED_COMMENTS.md#c203 */
    shallow?: boolean;
    /* see docs/EXTRACTED_COMMENTS.md#c204 */
    attachToActiveContext?: boolean;
};

/* see docs/EXTRACTED_COMMENTS.md#c205 */
class RenderInvalidationRegistry {
    private flushScheduled = false;

    /* see docs/EXTRACTED_COMMENTS.md#c206 */
    private markAndCollect(
        reason: RenderInvalidationReason,
        impactedIds?: Iterable<string>,
        shallow: boolean = false,
    ): void {
        const store = useStore();
        const seen = new Set<string>();
        if (!impactedIds) return;

        // Normalize incoming ids
        const baseIds: string[] = [];
        for (const id of impactedIds) {
            if (id) baseIds.push(id);
        }

        // see docs/EXTRACTED_COMMENTS.md#c207
        const autoSizeAncestors = baseIds.length > 0
            ? collectAutoSizeAncestorsForMany(baseIds)
            : [];

        const allIds = [...baseIds, ...autoSizeAncestors];

        const markOverlapPeers = (id: string) => {
            const parentId = getParent(id);
            if (!parentId) return;
            const parent = store.components[parentId];
            if (!parent || !Array.isArray(parent.children)) return;
            for (const child of parent.children) {
                const siblingId = (child as any)?.props?.id ?? (child as any)?.id;
                if (!siblingId || siblingId === id || seen.has(siblingId)) continue;
                if (!store.components[siblingId]) continue;
                seen.add(siblingId);
            }
        };

        const processId = (id: string) => {
            if (!id || seen.has(id)) return;
            if (!store.components[id]) return;
            seen.add(id);
            markOverlapPeers(id);
        };

        const walkSubtreeIterative = (rootId: string) => {
            // Iterative DFS to avoid call-stack overflow on deep component trees.
            const stack: string[] = [rootId];
            while (stack.length > 0) {
                const id = stack.pop()!;
                if (!id || seen.has(id)) continue;
                const comp: any = store.components[id];
                if (!comp) continue;
                processId(id);

                if (Array.isArray(comp.children)) {
                    for (const child of comp.children) {
                        const childId = (child as any)?.props?.id ?? (child as any)?.id;
                        if (childId && !seen.has(childId)) {
                            stack.push(childId);
                        }
                    }
                }
            }
        };

        for (const id of allIds) {
            if (shallow) {
                processId(id);
            } else {
                walkSubtreeIterative(id);
            }
        }

        // Mark collected ids once and enqueue geometry updates
        markForUpdate(seen);
        const cause = `invalidation:${reason}`;
        for (const id of seen) {
            postUpdateGeometry(id, cause);
        }
    }

    private scheduleFlush(): void {
        if (this.flushScheduled) return;
        this.flushScheduled = true;
        queueMicrotask(() => this.flush());
    }

    public requestRender(
        reason: RenderInvalidationReason,
        impactedIds?: Iterable<string>,
        options: InvalidationOptions = {}
    ) {
        // Counted at scheduling time, not post time — see #c723.
        noteRenderWork();
        // see docs/EXTRACTED_COMMENTS.md#c581
        if (options.attachToActiveContext && !options.force && impactedIds) {
            const ctxId = updateCoordinator.getActiveContextId();
            if (ctxId) {
                const shallow = options.shallow === true;
                if (updateCoordinator.enqueueInvalidation(ctxId, reason, impactedIds, shallow)) {
                    return;
                }
            }
        }

        this.markAndCollect(reason, impactedIds, options.shallow === true);
        this.scheduleFlush();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c582 */
    public requestRenderBatch(
        entries: Array<{ reason: RenderInvalidationReason; ids: Iterable<string>; shallow?: boolean }>,
        options: InvalidationOptions = {}
    ) {
        if (entries.length === 0) return;
        noteRenderWork();

        for (const entry of entries) {
            this.markAndCollect(
                entry.reason,
                entry.ids,
                entry.shallow === true || options.shallow === true
            );
        }

        this.scheduleFlush();
    }

    /* see docs/EXTRACTED_COMMENTS.md#c800 */
    public flush() {
        if (!this.flushScheduled) return;
        this.flushScheduled = false;
        void postRenderRequest();
    }
}

export const renderInvalidationRegistry = new RenderInvalidationRegistry();
